import React, {
  createContext,
  useState,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { HttpContext } from './HttpContext';
import { ROLES } from '../constants/roles';
import sentry from '../sentry';
const MeContext = createContext({
  state: {
    account: null,
    organisationStatus: null,
    trialExpiry: null,
    watermarkEnabled: null,
    watermarkFree: null,
    entities: null,
    subscribed: null,
    subscriptionRequired: null,
    filterEntity: null,
    passTemplates: null,
    impersonation: null,
    invitations: null,
    invitationLimit: null,
    vtapAutomaticSetupEnabled: null,
    eventsEnabled: null,
    status: 'idle',
    error: null,
    smsAndEmailMessagingEnabled: null,
    newsletterPopUpAccepted: null,
    billingEnabled: null,
    languagesSectionEnabled: null,
    builderToggleEnabled: null,
    localisationEnabled: null,
    cardlessTrialStartedAt: null,
    cardlessTrialEnabled: null,
    passBindingEnabled: null,
    appleEnhancedDesignEnabled: null,
    passBuilderEnabled: null,
    passBuilderEnabledInternal: null,
    onboardingChecklistEnabled: null,
    onboardingCompleted: null,
    onboardingQuestionnaireEnabled: null,
    onboardingQuestionnaireCompleted: null,
    prebuiltTemplatesEnabled: null,
    firstPassImprovementsEnabled: null,
    expressivePassRolloutEnabled: null,
    readOnlyDashboardEnabled: null,
    primaryUseCase: null,
    industry: null,
  },
  updateState: () => {},
  isSetUpConfirmed: () => {},
  isOrganisationActive: () => {},
});
const { Provider } = MeContext;

const MeProvider = ({ children }) => {
  const { authAxios } = useContext(HttpContext);
  const fetchIdRef = useRef(0);
  const [state, setState] = useState({
    account: null,
    organisationStatus: null,
    trialExpiry: null,
    watermarkEnabled: null,
    watermarkFree: null,
    entities: null,
    subscribed: null,
    subscriptionRequired: null,
    filterEntity: null,
    passTemplates: null,
    impersonation: null,
    invitations: null,
    invitationLimit: null,
    vtapAutomaticSetupEnabled: null,
    eventsEnabled: null,
    status: 'idle',
    error: null,
    smsAndEmailMessagingEnabled: null,
    newsletterPopUpAccepted: null,
    billingEnabled: null,
    languagesSectionEnabled: null,
    builderToggleEnabled: null,
    localisationEnabled: null,
    cardlessTrialStartedAt: null,
    cardlessTrialEnabled: null,
    passBindingEnabled: null,
    appleEnhancedDesignEnabled: null,
    passBuilderEnabled: null,
    passBuilderEnabledInternal: null,
    onboardingChecklistEnabled: null,
    onboardingCompleted: null,
    onboardingQuestionnaireEnabled: null,
    onboardingQuestionnaireCompleted: null,
    prebuiltTemplatesEnabled: null,
    firstPassImprovementsEnabled: null,
    expressivePassRolloutEnabled: null,
    readOnlyDashboardEnabled: null,
    primaryUseCase: null,
    industry: null,
  });

  function formatInvitations(invitations) {
    return invitations.map((invitation) => ({
      id: invitation.id,
      emailVerified: invitation.email_verified,
      entityId: invitation.entity_id,
      companyName: invitation.company_name,
      firstName: invitation.first_name,
      lastName: invitation.last_name,
      email: invitation.email,
      role: invitation.role,
    }));
  }

  function updateStateHandler(update) {
    setState((previous) => ({ ...previous, ...update }));
  }

  function updateFilterEntityHandler(filterEntity) {
    updateStateHandler({ filterEntity });
  }

  function isSetUpConfirmed() {
    const { account } = state;
    return account?.is_active;
  }

  function accountDashboardPassTemplateFilter() {
    return state?.account?.filters?.dashboardMeta?.passTemplateFilter;
  }

  function isOrganisationActive() {
    const { organisationStatus, readOnlyDashboardEnabled } = state;
    if (organisationStatus === 'trial' || organisationStatus === 'live') {
      return true;
    }
    return Boolean(
      readOnlyDashboardEnabled &&
      ['trial_expired', 'locked'].includes(organisationStatus)
    );
  }

  function userDashboardTimeFilter() {
    return state?.account?.filters?.dashboardMeta?.timeFilter;
  }

  function isAdmin() {
    return state?.account?.role.includes('admin');
  }

  function isOrgAdmin() {
    return state?.account?.role === ROLES.org_admin.value;
  }

  const fetchData = useCallback(() => {
    const fetchId = ++fetchIdRef.current;
    setTimeout(async () => {
      if (fetchId === fetchIdRef.current) {
        try {
          const response = await authAxios.get(`accounts/me`);
          const account = response?.data?.account;
          const organisationStatus = response?.data?.organisation_status;
          const trialExpiry = response?.data?.trial_expiry;
          const entities = response?.data?.entities;
          const passTemplates = response?.data?.pass_templates;
          const invitations = response?.data?.invitations;
          const invitationLimit = account?.invitation_limit;
          const formattedInvitations = formatInvitations(invitations);
          const subscribed = response?.data?.subscribed;
          const subscriptionRequired = response?.data?.subscription_required;
          const eventsEnabled = response?.data?.events_enabled;
          const billingEnabled = response?.data?.billing_enabled;
          const languagesSectionEnabled =
            response?.data?.languages_section_enabled;
          const builderToggleEnabled = response?.data?.builder_toggle_enabled;
          const localisationEnabled = response?.data?.localisation_enabled;
          const cardlessTrialStartedAt =
            response?.data?.cardless_trial_started_at;
          const cardlessTrialEnabled = response?.data?.cardless_trial_enabled;
          const passBindingEnabled = response?.data?.pass_binding_enabled;
          const appleEnhancedDesignEnabled =
            response?.data?.apple_enhanced_design_enabled;
          const passBuilderEnabled = response?.data?.pass_builder_enabled;
          const passBuilderEnabledInternal =
            response?.data?.pass_builder_enabled_internal;
          const onboardingChecklistEnabled =
            response?.data?.onboarding_checklist_enabled;
          const onboardingCompleted = response?.data?.onboarding_completed;
          const onboardingQuestionnaireEnabled =
            response?.data?.onboarding_questionnaire_enabled;
          const onboardingQuestionnaireCompleted =
            response?.data?.onboarding_questionnaire_completed;
          const prebuiltTemplatesEnabled =
            response?.data?.prebuilt_templates_enabled;
          const firstPassImprovementsEnabled =
            response?.data?.first_pass_improvements_enabled;
          const expressivePassRolloutEnabled =
            response?.data?.expressive_pass_rollout_enabled;
          const readOnlyDashboardEnabled =
            response?.data?.read_only_dashboard_enabled;
          const primaryUseCase = response?.data?.primary_use_case;
          const industry = response?.data?.industry;
          updateStateHandler({
            account,
            entities,
            organisationStatus,
            trialExpiry,
            subscribed,
            subscriptionRequired,
            passTemplates,
            invitations: formattedInvitations,
            invitationLimit,
            status: 'resolved',
            error: null,
            billingEnabled,
            languagesSectionEnabled,
            eventsEnabled,
            builderToggleEnabled,
            localisationEnabled,
            cardlessTrialStartedAt,
            cardlessTrialEnabled,
            passBindingEnabled,
            appleEnhancedDesignEnabled,
            passBuilderEnabled,
            passBuilderEnabledInternal,
            onboardingChecklistEnabled,
            onboardingCompleted,
            onboardingQuestionnaireEnabled,
            onboardingQuestionnaireCompleted,
            prebuiltTemplatesEnabled,
            firstPassImprovementsEnabled,
            expressivePassRolloutEnabled,
            readOnlyDashboardEnabled,
            primaryUseCase,
            industry,
          });
        } catch (onError) {
          sentry.sendError(onError);
          console.log(onError);
        }
      }
    }, 0);
  }, [authAxios]);

  return (
    <Provider
      value={{
        state,
        updateState: updateStateHandler,
        isSetUpConfirmed,
        isOrganisationActive,
        accountDashboardPassTemplateFilter,
        userDashboardTimeFilter,
        updateFilterEntity: updateFilterEntityHandler,
        isAdmin,
        isOrgAdmin,
        fetchData,
        formatInvitations,
      }}
    >
      {children}
    </Provider>
  );
};

export { MeContext, MeProvider };
